import {
  Box,
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Typography,
  IconButton,
  TableContainer,
  CircularProgress,
} from '@mui/material';

import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import Scrollbar from 'src/components/scrollbar';
import UpdatedText from 'src/components/ui-kit/updated-text';

import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { Pagination } from 'src/type/utils';
import { ProductType } from 'src/type/product';
import { UserLevelType } from 'src/type/user';
import { UserLevelProduct } from 'src/type/user-level-product';
import { UserLevelPrice } from 'src/type/user-level-price';
import { ProductPriceLog } from 'src/type/product-price-log';

import { useBoolean } from 'src/hooks/use-boolean';
import Iconify from 'src/components/iconify';
import { fTime } from 'src/utils/format-time';
import SettingSourcePriceTableRow from './setting-source-price-table-row';
import { useRealtimeLevelPrices } from './use-realtime-level-prices';
import { getLatestRealtimeUpdate } from './realtime-net-price';
import CurrentPriceCard from './current-price-card';
import CreateCustomPriceDialogDialog from './components/create-custom-price-dialog';

// ----------------------------------------------------------------------

export default function SettingSourcePrice() {
  const create = useBoolean(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [products, setProducts] = useState<ProductType[]>([]);
  const [userLevels, setUserLevels] = useState<UserLevelType[]>([]);
  const [userLevelProducts, setUserLevelProducts] = useState<UserLevelProduct[]>([]);
  const [userLevelPrices, setUserLevelPrices] = useState<UserLevelPrice[]>([]);
  const [productPriceLogs, setProductPriceLogs] = useState<ProductPriceLog[]>([]);
  const [loading, setLoading] = useState(true);

  // Customer-facing prices per level, live from the same node the mobile app reads.
  const { prices: realtimeLevelPrices } = useRealtimeLevelPrices();
  const realtimeUpdatedAt = useMemo(
    () => getLatestRealtimeUpdate(realtimeLevelPrices),
    [realtimeLevelPrices]
  );

  const latestUpdatedAt = useMemo(() => {
    const allUpdates = [
      ...userLevelProducts.map((ulp) => ({
        time: new Date(ulp.updatedAt || ulp.createdAt).getTime(),
        user: ulp.updatedByUser,
      })),
      ...userLevelPrices.map((ulp) => ({
        time: new Date(ulp.updatedAt || ulp.createdAt).getTime(),
        user: ulp.updatedByUser,
      })),
    ];

    if (allUpdates.length === 0) {
      return { time: Date.now(), user: undefined };
    }

    const latestUpdate = allUpdates.reduce((latest, current) =>
      current.time > latest.time ? current : latest
    );

    return {
      time: latestUpdate.time,
      user: latestUpdate.user
        ? `${latestUpdate.user.firstName} ${latestUpdate.user.lastName}`
        : 'admin',
    };
  }, [userLevelProducts, userLevelPrices]);

  // Helper functions for updating state
  const handleUserLevelProductsUpdate = useCallback(
    (updatedUserLevelProducts: UserLevelProduct[]) => {
      setUserLevelProducts(updatedUserLevelProducts);
    },
    []
  );

  const handleUserLevelPricesUpdate = useCallback((updatedUserLevelPrices: UserLevelPrice[]) => {
    setUserLevelPrices(updatedUserLevelPrices);
  }, []);

  const handleUserLevelUpdate = useCallback((updatedUserLevel: UserLevelType) => {
    setUserLevels((prevLevels) =>
      prevLevels.map((level) => (level.id === updatedUserLevel.id ? updatedUserLevel : level))
    );
  }, []);

  // Helper function to get latest price for a product
  const getLatestPriceForProduct = useCallback(
    (productId: string) => productPriceLogs.find((log) => log.productId === productId),
    [productPriceLogs]
  );

  // Helper function to format price
  const formatPrice = useCallback((price: number | undefined) => {
    if (price === undefined || price === null || price === 0) return '-';
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }, []);

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  const getData = useCallback(async (isInitialLoad = true) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      const [
        { data: productList },
        { data: userLevelList },
        { data: userLevelProductList },
        { data: userLevelPriceList },
      ] = await Promise.all([
        axios.get<Pagination<ProductType>>(endpoints.product.get),
        axios.get(endpoints.userLevel.get),
        axios.get(endpoints.userLevelProduct.get),
        axios.get(endpoints.userLevelPrice.get),
      ]);

      // Get latest price logs for all products
      const priceLogs = await Promise.all(
        productList.results.map(async (product) => {
          try {
            const { data: latestPriceLog } = await axios.get<ProductPriceLog>(
              endpoints.productPriceLog.getLatest,
              {
                params: {
                  productId: product.id,
                },
              }
            );
            return latestPriceLog;
          } catch (error) {
            console.error(`Error fetching price log for product ${product.id}:`, error);
            return null;
          }
        })
      );

      const validPriceLogs = priceLogs.filter((log): log is ProductPriceLog => log !== null);

      const sortedProducts = productList.results.sort((a, b) => a.sequence - b.sequence);
      setProducts(sortedProducts);
      setUserLevels(userLevelList);
      setUserLevelProducts(userLevelProductList);
      setUserLevelPrices(userLevelPriceList);
      setProductPriceLogs(validPriceLogs);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getData(true);

    const intervalId = setInterval(() => {
      getData(false);
    }, 10_000);

    return () => clearInterval(intervalId);
  }, [getData]);

  return (
    <>
      <CurrentPriceCard
        products={products}
        loading={loading}
        formatPrice={formatPrice}
        getLatestPriceForProduct={getLatestPriceForProduct}
      />
      <Card sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack>
              <Typography variant="h6">แหล่งอ้างอิงราคา</Typography>
              <Typography variant="caption" color="text.secondary">
                <UpdatedText
                  user={latestUpdatedAt.user}
                  updatedAt={new Date(latestUpdatedAt.time).toISOString()}
                />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ราคาตัวหนาคือราคาสุทธิที่ลูกค้าเห็นในแอป (ต้นทาง + ส่วนต่าง)
                {realtimeUpdatedAt ? ` · อัปเดต ${fTime(realtimeUpdatedAt, 'HH:mm:ss')}` : ''}
              </Typography>
            </Stack>
            <Button variant="contained" onClick={create.onTrue}>
              กำหนดราคา
            </Button>
          </Stack>
          <TableContainer sx={{ position: 'relative', overflow: 'auto' }}>
            <Scrollbar ref={scrollContainerRef}>
              <Table sx={{ minWidth: 320 * products.length + 200 }}>
                <TableHead>
                  <TableRow>
                    {/* Main header row */}
                    <TableCell
                      align="center"
                      rowSpan={2}
                      sx={{
                        fontWeight: 'fontWeightSemiBold',
                        backgroundColor: 'background.neutral',
                        width: 70,
                        borderRight: '1px solid',
                        borderRightColor: 'divider',
                      }}
                    >
                      ระดับ
                    </TableCell>
                    <TableCell
                      align="center"
                      rowSpan={2}
                      sx={{
                        fontWeight: 'fontWeightSemiBold',
                        backgroundColor: 'background.neutral',
                        width: 120,
                        borderRight: '1px solid',
                        borderRightColor: 'divider',
                      }}
                    >
                      ลูกค้า
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: 'background.neutral',
                        fontSize: 18,
                      }}
                    >
                      การซื้อขายราคา
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {/* Sub header row */}
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'fontWeightSemiBold',
                        p: 0,
                      }}
                    >
                      <Box sx={{ display: 'flex' }}>
                        {products
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((product, productIndex) => {
                            const isNotLastProduct = productIndex < products.length - 1;

                            return (
                              <Box
                                key={product.id}
                                sx={{
                                  minWidth: 320,
                                  flex: 1,
                                  p: 1,
                                  textAlign: 'center',
                                  backgroundColor: 'background.neutral',
                                  ...(isNotLastProduct && {
                                    borderRight: '1px solid',
                                    borderRightColor: 'divider',
                                  }),
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 'fontWeightSemiBold' }}
                                >
                                  {product.name}
                                  {!product.isEnable && (
                                    <Typography
                                      variant="caption"
                                      sx={{ ml: 0.5, color: 'error.main' }}
                                      component="span"
                                    >
                                      (ปิดอยู่)
                                    </Typography>
                                  )}
                                </Typography>
                                <Box sx={{ display: 'flex', mt: 0.5 }}>
                                  <Box
                                    sx={{
                                      flex: 0.6,
                                      py: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 'fontWeightMedium' }}
                                    >
                                      ปิด/เปิด
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      flex: 1,
                                      py: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 'fontWeightMedium' }}
                                    >
                                      รับซื้อ
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1, py: 0.5 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 'fontWeightMedium' }}
                                    >
                                      ขาย
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 10 }}>
                        <Box
                          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        >
                          <CircularProgress size={40} sx={{ mb: 2 }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && userLevels.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 10 }}>
                        <Typography variant="body2" color="text.secondary">
                          ไม่มีข้อมูล
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && products.length === 0 && userLevels.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 10 }}>
                        <Typography variant="body2" color="text.secondary">
                          ไม่มีข้อมูลสินค้า
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    userLevels.length > 0 &&
                    products.length > 0 &&
                    userLevels.map((level, index) => (
                      <SettingSourcePriceTableRow
                        key={level.id}
                        level={level}
                        products={products}
                        userLevelProducts={userLevelProducts}
                        userLevelPrices={userLevelPrices}
                        productPriceLogs={productPriceLogs}
                        realtimePrices={realtimeLevelPrices[level.id] ?? undefined}
                        onUserLevelProductsUpdate={handleUserLevelProductsUpdate}
                        onUserLevelPricesUpdate={handleUserLevelPricesUpdate}
                        onUserLevelUpdate={handleUserLevelUpdate}
                      />
                    ))}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          {/* Navigation arrows */}
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
            <IconButton
              onClick={handleScrollLeft}
              sx={{
                backgroundColor: 'background.neutral',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Iconify icon="eva:arrow-ios-back-fill" />
            </IconButton>
            <IconButton
              onClick={handleScrollRight}
              sx={{
                backgroundColor: 'background.neutral',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Iconify icon="eva:arrow-ios-forward-fill" />
            </IconButton>
          </Stack>
        </Stack>
      </Card>

      <CreateCustomPriceDialogDialog
        open={create.value}
        onClose={create.onFalse}
        handleReload={getData}
        products={products}
      />
    </>
  );
}
